import {
    experienceAllowedOptions,
    disciplines,
    horseQualifications,
    HORSE_AVAILABILITY_PART_TIME_OPTION_UNLIMITED,
    RIDINGS_PER_WEEK_MODERATE,
    EXPERIENCE_LEVEL_BEGINNER,
    EXPERIENCE_LEVEL_ADVANCED,
    EXPERIENCE_LEVEL_EXPERIENCED,
    EXPERIENCE_LEVEL_VERY_EXPERIENCED,
    INTEREST_CARE,
    INTEREST_WALK,
    INTEREST_BASIC,
    INTEREST_RIDING,
    INTEREST_TRAINING,
    HORSE_QUALIFICATION_ABSENT,
    HORSE_QUALIFICATION_BASIC_TRAINING,
    INTEREST_COMPETITION,
    HORSE_QUALIFICATION_TOURNAMENT_EXPERIENCE,
} from '../../marketplace-custom-config';
import { formatDate } from '../../containers/ListingPage/Sections/Helpers';
import { sendAiPrompt } from '../../util/api';
import { transformHorseType } from '../../mixpanel/trackingTransformers';
import {
    HORSE_TYPE_FREIZEITPFERD,
    HORSE_TYPE_HORSE_KIND,
    HORSE_TYPE_JUNGPFERD_IN_AUSBILDUNG,
    HORSE_TYPE_SENIOR,
    DESCRIPTION_HORSE,
    DESCRIPTION_RIDER,
    LOCATION,
    PRICING,
    PHOTOS,
    BOOSTING_CH,
    BOOSTING_DE,
    LISTING_PAGE_PARAM_TYPE_NEW,
    LISTING_PAGE_PARAM_TYPE_DRAFT,
    LISTING_PAGE_PARAM_TYPE_DRAFT_INTERRUPTED,
    EDIT_LISTING_STEPS_MAP,
    EDIT_LISTING_STEPS_PER_TAB,
    EDIT_LISTING_TAB_START_DATE,
    EDIT_LISTING_TAB_REQUIREMENTS,
    EDIT_LISTING_TAB_EXPERIENCE,
    EDIT_LISTING_TAB_ACTIVITIES,
    EDIT_LISTING_TAB_QUALIFICATIONS,
} from './config';

export const isDraftInterrupted = params =>
    params.type === LISTING_PAGE_PARAM_TYPE_DRAFT_INTERRUPTED;

export const isDraft = params => params.type === LISTING_PAGE_PARAM_TYPE_DRAFT;
export const isNewListing = params => params.type === LISTING_PAGE_PARAM_TYPE_NEW;
export const isBoostingTab = tab => tab === BOOSTING_CH || tab === BOOSTING_DE;

export const getPositionFromTypeURL = type =>
    type && EDIT_LISTING_STEPS_MAP[type] ? EDIT_LISTING_STEPS_MAP[type] : { tab: 0, step: 0 };

const getOneTabProgress = (usedTab, tab, step) => {
    const allStepsCount = EDIT_LISTING_STEPS_PER_TAB[usedTab];
    if (usedTab < tab) {
        return {
            completedSteps: allStepsCount,
            percentage: 100,
            allStepsCount,
        };
    } else if (usedTab > tab) {
        return {
            completedSteps: 0,
            percentage: 0,
            allStepsCount,
        };
    } else {
        return {
            completedSteps: step,
            percentage: Number(((step / allStepsCount) * 100).toFixed(2)),
            allStepsCount,
        };
    }
};

const getTabsProgress = value => {
    const [tab, step] = value.split('-');

    return Object.keys(EDIT_LISTING_STEPS_PER_TAB).reduce(
        (acc, usedTab) => ({
            ...acc,
            [usedTab]: getOneTabProgress(usedTab, Number(tab), Number(step)),
        }),
        {}
    );
};

export const tabParams = (tab, savedProgress) => {
    const progressDetails = getTabsProgress(savedProgress);

    const indexDictionary = {
        [DESCRIPTION_HORSE]: 1,
        [DESCRIPTION_RIDER]: 1,
        [LOCATION]: 2,
        [PHOTOS]: 3,
        [PRICING]: 4,
    };
    const index = indexDictionary[tab];
    return {
        ...progressDetails[index],
        index,
    };
};

export const getTheHigherProgress = (oldProgress, newProgress) => {
    const [tab1, step1] = oldProgress.split('-').map(Number);
    const [tab2, step2] = newProgress.split('-').map(Number);

    if (tab1 > tab2) {
        return oldProgress;
    } else if (tab1 < tab2) {
        return newProgress;
    } else {
        if (step1 > step2) {
            return oldProgress;
        } else {
            return newProgress;
        }
    }
};

const createPromptText = (currentListing, intl) => {
    const {
        title,

        publicData = {},
    } = currentListing?.attributes || {};

    const {
        horseType,
        gender,
        hight,
        breed,
        color,
        age,
        publicAddress,
        skillsGeneral,
        skillsExperience,
        startDate,
        startDateISO,
        endDateISO,
        availability,
        desiredExperience,
        desiredDisciplines = [],
        activities,
        userCountry = 'CH',
        requiredQualificationLevel,
    } = publicData;

    const availabilityKeys = {
        indefinite: 'Ab sofort',
        chosenDay: `Ab ${formatDate(startDateISO)}`,
        temporary: `${formatDate(startDateISO)} bis ${formatDate(endDateISO)}`,
    };

    const header = `
        Erstelle mir eine Inserate-Beschreibung aus den folgenden Informationen:
        Pferd sucht Reiter:in 
        Anrede: Du
        Rolle: Pferd
    `;
    const name = `Name Pferd: ${title}`;
    const genderH = `Geschlecht Pferd: ${gender}`;
    const height = `Stockmass Pferd:${hight} cm`;
    const raceMaybe = breed ? `Rasse Pferd: ${breed}` : '';
    const colorMaybe = color ? `Rasse Pferd: ${color}` : '';
    const horseKind = `Art Pferd ${transformHorseType(horseType)}`;
    const ageH = `Alter Pferd: ${age} Jahre`;
    const location = `Suche in: ${publicAddress}`;
    const activitiesH = `Aktivitäten Reiter: ${(activities || []).join(', ')}`;
    const riderAvailability = startDate
        ? `Verfügbarkeit Reiter: ${availabilityKeys[startDate]}`
        : '';
    const riderFrequency = `Häufigkeit Reiter: ${intl.formatMessage({
        id: `EditListingDescriptionForm.desiredAvailabilityRadioButton-${availability}`,
    })}`;
    const exp = `Niveau Reiter: ${
        experienceAllowedOptions.find(o => o.key == desiredExperience)?.label
    }`;

    const germanDisciplines = desiredDisciplines.map(d => {
        const foundDiscipline = disciplines[userCountry].find(dd => dd.id == d);
        return foundDiscipline ? foundDiscipline.label : '';
    });

    const discipline = `Gewünschte Disziplinen Reiter: ${germanDisciplines.join(', ')}`;
    const mappedQualifications = Object.values(horseQualifications).find(
        ({ key }) => key === requiredQualificationLevel
    );
    const qualificationsH = `Gewünschte Qualifikationen Reiter: ${mappedQualifications?.label ||
        'Keine'}`;
    const requirments = `Besondere Anforderungen Reiter: ${[
        ...(skillsGeneral || []),
        ...(skillsExperience || []),
    ]
        .flat()
        .join(', ')}`;

    const prompt = `
${header}
${name}
${genderH}
${height}
${raceMaybe}
${colorMaybe}
${horseKind}
${ageH}
Sucht: Reiter:in
${location}
${activitiesH}
${riderAvailability}
${riderFrequency}
${exp}
${discipline}
${qualificationsH}
${requirments}`;

    return prompt;
};

export const promptUserDescriptions = async (currentListing, intl) => {
    try {
        const prompt = createPromptText(currentListing, intl);
        const data = await sendAiPrompt(JSON.stringify({ prompt, type: 'horse' }));

        if (data.error) {
            throw new Error(data.error.message);
        }

        if (!data.text) {
            throw new Error('Text generation failed.');
        }

        return data.text;
    } catch (error) {
        console.log(error);
    }
};

export const scrollToTab = (tabPrefix, tabId) => {
    const el = document.querySelector(`#${tabPrefix}_${tabId}`);
    if (el) {
        el.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
        });
    }
};

export const shouldPrefilValueBeShown = (fieldName, form) => {
    if (!form || !fieldName) return false;
    const { initialValues, visited } = form.getState() || {};

    if (typeof initialValues !== 'object') return false;
    if (typeof visited !== 'object') return false;

    if (initialValues[fieldName]) return false;
    if (visited[fieldName]) return false;
    return true;
};

export const getPrefilledValueFromHorseType = ({ values, tab }) => {
    const { horseType, activities } = values;

    const prefilledValuesConfig = {
        [HORSE_TYPE_JUNGPFERD_IN_AUSBILDUNG]: {
            get [EDIT_LISTING_TAB_START_DATE]() {
                return HORSE_AVAILABILITY_PART_TIME_OPTION_UNLIMITED;
            },
            get [EDIT_LISTING_TAB_REQUIREMENTS]() {
                return RIDINGS_PER_WEEK_MODERATE;
            },
            get [EDIT_LISTING_TAB_EXPERIENCE]() {
                return EXPERIENCE_LEVEL_EXPERIENCED;
            },
            get [EDIT_LISTING_TAB_ACTIVITIES]() {
                return [INTEREST_CARE, INTEREST_WALK, INTEREST_BASIC];
            },
            get [EDIT_LISTING_TAB_QUALIFICATIONS]() {
                return HORSE_QUALIFICATION_BASIC_TRAINING;
            },
        },
        [HORSE_TYPE_FREIZEITPFERD]: {
            get [EDIT_LISTING_TAB_START_DATE]() {
                return HORSE_AVAILABILITY_PART_TIME_OPTION_UNLIMITED;
            },
            get [EDIT_LISTING_TAB_REQUIREMENTS]() {
                return RIDINGS_PER_WEEK_MODERATE;
            },
            get [EDIT_LISTING_TAB_EXPERIENCE]() {
                return EXPERIENCE_LEVEL_ADVANCED;
            },
            get [EDIT_LISTING_TAB_ACTIVITIES]() {
                return [
                    INTEREST_CARE,
                    INTEREST_WALK,
                    INTEREST_BASIC,
                    INTEREST_RIDING,
                    INTEREST_TRAINING,
                ];
            },
            get [EDIT_LISTING_TAB_QUALIFICATIONS]() {
                return HORSE_QUALIFICATION_ABSENT;
            },
        },
        [HORSE_TYPE_HORSE_KIND]: {
            get [EDIT_LISTING_TAB_START_DATE]() {
                return HORSE_AVAILABILITY_PART_TIME_OPTION_UNLIMITED;
            },
            get [EDIT_LISTING_TAB_REQUIREMENTS]() {
                return RIDINGS_PER_WEEK_MODERATE;
            },
            get [EDIT_LISTING_TAB_EXPERIENCE]() {
                return EXPERIENCE_LEVEL_EXPERIENCED;
            },
            get [EDIT_LISTING_TAB_ACTIVITIES]() {
                return [
                    INTEREST_CARE,
                    INTEREST_WALK,
                    INTEREST_BASIC,
                    INTEREST_RIDING,
                    INTEREST_TRAINING,
                    INTEREST_COMPETITION,
                ];
            },
            get [EDIT_LISTING_TAB_QUALIFICATIONS]() {
                const hasCompetitionActivity = activities?.includes(INTEREST_COMPETITION);
                if (hasCompetitionActivity) {
                    return HORSE_QUALIFICATION_TOURNAMENT_EXPERIENCE;
                }
                return HORSE_QUALIFICATION_BASIC_TRAINING;
            },
        },
        [HORSE_TYPE_SENIOR]: {
            get [EDIT_LISTING_TAB_START_DATE]() {
                return HORSE_AVAILABILITY_PART_TIME_OPTION_UNLIMITED;
            },
            get [EDIT_LISTING_TAB_REQUIREMENTS]() {
                return RIDINGS_PER_WEEK_MODERATE;
            },
            get [EDIT_LISTING_TAB_EXPERIENCE]() {
                return EXPERIENCE_LEVEL_BEGINNER;
            },
            get [EDIT_LISTING_TAB_ACTIVITIES]() {
                return [INTEREST_CARE, INTEREST_WALK, INTEREST_BASIC];
            },
            get [EDIT_LISTING_TAB_QUALIFICATIONS]() {
                return HORSE_QUALIFICATION_ABSENT;
            },
        },
    };
    return prefilledValuesConfig?.[horseType]?.[tab];
};
